/** この端末で応答したローカルMCPの版だけを取得する。認証・導入判定には使わない。 */
import {
  LOCAL_MCP_STATUS_URL,
  LOCAL_MCP_VERSION,
  parseLocalMcpStatus,
} from '../../../backend/mcp/local-status-protocol';
import { isMcpClientOutdated } from '../../../backend/mcp/client-version';
import { CURRENT_MCP_VERSION } from './mcpClientVersion';

export const LOCAL_MCP_STATUS_CHANGED_EVENT = 'acecollect:local-mcp-status-changed';
export const LOCAL_MCP_STATUS_TIMEOUT_MS = 1500;
export const LOCAL_MCP_MANUAL_TIMEOUT_MS = 30_000;
const MAX_STATUS_BYTES = 4096;

export type LocalMcpDeviceStatus =
  | { kind: 'unavailable' }
  | { kind: 'current' | 'outdated'; version: string; checkedAt: number };

type NetworkPermission = PermissionState | 'unsupported' | 'unavailable';

/** 型定義に未収録の権限名も、通常のPermissions APIへ渡し、戻り値を検証する。 */
export async function localNetworkPermission(): Promise<NetworkPermission> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unsupported';
  for (const name of ['loopback-network', 'local-network-access']) {
    try {
      const result: unknown = await Reflect.apply(
        navigator.permissions.query,
        navigator.permissions,
        [{ name }],
      );
      if (result && typeof result === 'object' && 'state' in result) {
        const state: unknown = result.state;
        if (state === 'granted' || state === 'denied' || state === 'prompt') return state;
      }
      return 'unavailable';
    } catch (error) {
      if (
        error instanceof TypeError ||
        (error instanceof DOMException && error.name === 'NotSupportedError')
      )
        continue;
      return 'unavailable';
    }
  }
  return 'unsupported';
}

function requestNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** 未対応・拒否・通信失敗は不明。未導入や更新不要だと読み替えない。 */
export async function probeLocalMcpStatus(
  mode: 'automatic' | 'manual',
  signal?: AbortSignal,
): Promise<{ version: string } | null> {
  if (signal?.aborted) return null;
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(
    abort,
    mode === 'manual' ? LOCAL_MCP_MANUAL_TIMEOUT_MS : LOCAL_MCP_STATUS_TIMEOUT_MS,
  );
  let cancelPermission: (() => void) | undefined;
  try {
    if (mode === 'automatic') {
      const interrupted = new Promise<'unavailable'>((resolve) => {
        cancelPermission = () => resolve('unavailable');
        controller.signal.addEventListener('abort', cancelPermission, { once: true });
      });
      const permission = await Promise.race([localNetworkPermission(), interrupted]);
      if (permission !== 'granted') return null;
    }
    if (controller.signal.aborted) return null;
    const nonce = requestNonce();
    const response = await fetch(`${LOCAL_MCP_STATUS_URL}?nonce=${nonce}`, {
      method: 'GET',
      credentials: 'omit',
      redirect: 'error',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
    });
    if (
      controller.signal.aborted ||
      response.status !== 200 ||
      response.redirected ||
      !response.body ||
      !/^application\/json(?:\s*;|$)/i.test(response.headers.get('content-type') ?? '')
    )
      return null;
    const length = response.headers.get('content-length');
    if (length !== null && (!/^\d+$/.test(length) || Number(length) > MAX_STATUS_BYTES))
      return null;
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let total = 0;
    try {
      for (;;) {
        const next = await reader.read();
        if (controller.signal.aborted) return null;
        if (next.done) break;
        total += next.value.byteLength;
        if (total > MAX_STATUS_BYTES) return null;
        chunks.push(next.value);
      }
    } finally {
      await reader.cancel().catch(() => undefined);
      reader.releaseLock();
    }
    const bytes = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }
    const value: unknown = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
    return parseLocalMcpStatus(value, nonce);
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
    if (cancelPermission) controller.signal.removeEventListener('abort', cancelPermission);
    controller.abort();
  }
}

export function localMcpDeviceStatus(
  result: { version: string } | null,
  nowMs: number,
): LocalMcpDeviceStatus {
  return result &&
    LOCAL_MCP_VERSION.test(result.version) &&
    LOCAL_MCP_VERSION.test(CURRENT_MCP_VERSION)
    ? {
        kind: isMcpClientOutdated(result.version, CURRENT_MCP_VERSION) ? 'outdated' : 'current',
        version: result.version,
        checkedAt: nowMs,
      }
    : { kind: 'unavailable' };
}

/** 結果を流さず、Header自身に新しい応答を取得してもらう。 */
export function notifyLocalMcpStatusChanged(): void {
  window.dispatchEvent(new Event(LOCAL_MCP_STATUS_CHANGED_EVENT));
}
